import { __decorate } from "tslib";
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { action, computed } from 'mobx';
import { pxToRem } from '../../../es/_util/UnitConvertor';
import measureScrollbar from '../../../es/_util/measureScrollbar';
import TableHeaderCell from './TableHeaderCell';
import TableContext from './TableContext';
import { ColumnLock } from './enum';
import { getColumnKey, getColumnLock, getHeight, isStickySupport } from './utils';
import autobind from '../_util/autobind';
import TableHeaderRow from './TableHeaderRow';
let TableHeader = class TableHeader extends Component {
    saveRef(node) {
        this.node = node;
    }
    getHeaderNode() {
        return this.node;
    }
    handleTheadMouseEnter() {
        const { tableStore, } = this.context;
        tableStore.isHeaderHover = true;
    }
    handleTheadMouseLeave() {
        const { tableStore, } = this.context;
        tableStore.isHeaderHover = false;
    }
    getTrs() {
        const { lock, dataSet } = this.props;
        const { tableStore, } = this.context;
        const { prefixCls } = tableStore;
        const rows = this.getTableHeaderRows(this.groupedColumns);
        return rows.map((row, rowIndex) => {
            if (row.length) {
                const hasPlaceholder = tableStore.overflowY && rowIndex === 0 && lock !== ColumnLock.left;
                let prevColumn;
                const placeholderWidth = hasPlaceholder ? measureScrollbar() : 0;
                const tds = row.map((col, index, cols) => {
                    if (!col.hidden) {
                        const { column, rowSpan, colSpan, lastLeaf, children } = col;
                        const key = String(getColumnKey(column));
                        const props = {
                            key,
                            dataSet,
                            prevColumn,
                            column,
                            resizeColumn: lastLeaf,
                            getHeaderNode: this.getHeaderNode,
                        };
                        if (rowSpan > 1 || children) {
                            props.rowSpan = rowSpan;
                        }
                        if (colSpan > 1 || children) {
                            props.colSpan = colSpan;
                        }
                        prevColumn = lastLeaf;
                        if (isStickySupport() && tableStore.overflowX) {
                            const columnLock = getColumnLock(column.lock);
                            if (columnLock === ColumnLock.left) {
                                props.style = {
                                    left: pxToRem(col.left),
                                };
                                const next = cols[index + 1];
                                if (!next || getColumnLock(next.column.lock) !== ColumnLock.left) {
                                    props.className = `${prefixCls}-cell-fix-left-last`;
                                }
                            }
                            else if (columnLock === ColumnLock.right) {
                                props.style = {
                                    right: pxToRem(col.right + placeholderWidth),
                                };
                                const prev = cols[index - 1];
                                if (!prev || prev.column.lock !== ColumnLock.right) {
                                    props.className = `${prefixCls}-cell-fix-right-first`;
                                }
                            }
                        }
                        return (React.createElement(TableHeaderCell, Object.assign({}, props)));
                    }
                    return undefined;
                });
                if (hasPlaceholder) {
                    const placeHolderProps = {
                        key: 'fixed-column',
                        rowSpan: rows.length,
                    };
                    const classList = [`${prefixCls}-cell`];
                    if (isStickySupport() && tableStore.overflowX) {
                        const hasColRightLock = tds.some(td => {
                            if (td) {
                                return td.props.column.lock === ColumnLock.right;
                            }
                            return false;
                        });
                        placeHolderProps.style = hasColRightLock ? { right: 0 } : {};
                        classList.push(`${prefixCls}-cell-fix-right`);
                    }
                    placeHolderProps.className = classList.join(' ');
                    tds.push(React.createElement("th", Object.assign({}, placeHolderProps), "\u00A0"));
                }
                return (React.createElement(TableHeaderRow, { key: String(rowIndex), rowIndex: rowIndex, tds: tds, rows: rows, lock: lock }));
            }
            return undefined;
        });
    }
    render() {
        const { tableStore: { prefixCls, overflowX, columnResizable, isHeaderHover, columnResizing, props: { border } }, } = this.context;
        const trs = this.getTrs();
        const theadProps = {
            ref: this.saveRef,
            className: classNames(`${prefixCls}-thead`, {
                [`${prefixCls}-column-resizable`]: columnResizable,
                [`${prefixCls}-column-group`]: trs && trs.length > 1,
                [`${prefixCls}-thead-hover`]: isHeaderHover || columnResizing,
            }),
        };
        if (!isStickySupport() && overflowX && !border) {
            theadProps.onMouseEnter = this.handleTheadMouseEnter;
            theadProps.onMouseLeave = this.handleTheadMouseLeave;
        }
        return (React.createElement("thead", Object.assign({}, theadProps), trs));
    }
    getHeight() {
        const { node } = this;
        if (node) {
            return getHeight(node);
        }
        return 0;
    }
    getTableHeaderRows(columns, currentRow = 0, rows = []) {
        rows[currentRow] = rows[currentRow] || [];
        columns.forEach(column => {
            const { hidden, rowSpan, colSpan, children } = column;
            if (!hidden) {
                if (rowSpan && rows.length < rowSpan) {
                    while (rows.length < rowSpan) {
                        rows.push([]);
                    }
                }
                if (children) {
                    this.getTableHeaderRows(children.columns, currentRow + rowSpan, rows);
                }
                if (colSpan !== 0) {
                    rows[currentRow].push(column);
                }
            }
        });
        return rows;
    }
    get groupedColumns() {
        const { tableStore } = this.context;
        const { lock } = this.props;
        switch (lock) {
            case ColumnLock.left:
            case true:
                return tableStore.leftGroupedColumns;
            case ColumnLock.right:
                return tableStore.rightGroupedColumns;
            default:
                return tableStore.groupedColumns;
        }
    }
};
TableHeader.displayName = 'TableHeader';
TableHeader.propTypes = {
    lock: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.oneOf([ColumnLock.right, ColumnLock.left]),
    ]),
};
TableHeader.contextType = TableContext;
__decorate([
    autobind
], TableHeader.prototype, "saveRef", null);
__decorate([
    autobind
], TableHeader.prototype, "getHeaderNode", null);
__decorate([
    autobind,
    action
], TableHeader.prototype, "handleTheadMouseEnter", null);
__decorate([
    autobind,
    action
], TableHeader.prototype, "handleTheadMouseLeave", null);
__decorate([
    computed
], TableHeader.prototype, "groupedColumns", null);
TableHeader = __decorate([
    observer
], TableHeader);
export default TableHeader;
